import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";

export function PublicNav() {
  const loc = useLocation();
  // Iter 22 (audit D4) — session-persistence fix. AuthContext already lives
  // at the App root, but the public nav previously rendered "Sign in / Try
  // free" regardless of state — which felt like a logged-out experience
  // even though the token was still in localStorage. Read `user` from
  // context; when present, swap to a "Go to app →" CTA.
  const { user } = useAuth();
  const isAuthed = Boolean(user);

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      className={`text-sm transition-colors ${loc.pathname === to ? "text-white" : "text-white/60 hover:text-white"}`}
    >
      {children}
    </Link>
  );

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="sticky top-0 z-50 w-full"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-8 lg:px-12">
        <Link to="/" className="flex items-center" data-testid="logo-home-link" aria-label="Symplee — home">
          <Wordmark variant="dark" size="md" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <NavLink to="/">Product</NavLink>
          <NavLink to="/pricing">Pricing</NavLink>
          <NavLink to="/about">About</NavLink>
          <a href="/#how-it-works" className="text-sm text-white/60 hover:text-white">
            How it works
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          {isAuthed ? (
            <Link to="/app" data-testid="navbar-go-to-app">
              <Button className="rounded-full bg-white px-5 text-black hover:bg-zinc-200">
                Go to app <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="hidden text-sm text-white/70 hover:text-white sm:inline" data-testid="navbar-login">
                Sign in
              </Link>
              <Link to="/signup" data-testid="navbar-signup">
                <Button className="rounded-full bg-white px-5 text-black hover:bg-zinc-200">
                  Try free for 7 days
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </motion.header>
  );
}
